import React, { useState, useEffect } from 'react';
import PlayerSilhouette from './PlayerSilhouette';

/**
 * PlayerTableCell - Default Player column cell used across all tables.
 * Displays: headshot, player name, MLB team abbreviation, eligible positions.
 */
function PlayerTableCell({
  player,
  leagueId,
  onPlayerClick,
  emptyLabel = 'Empty',
  size = 'md',
  asTd = true,
  tdStyle = {},
}) {
  const [imgError, setImgError] = useState(false);
  const photoUrl = player?.photoUrl || player?.headshotUrl || player?.image;
  useEffect(() => {
    setImgError(false);
  }, [player?.id, photoUrl]);
  const showPhoto = Boolean(photoUrl && !imgError);
  const headshotSize = size === 'sm' ? 28 : size === 'lg' ? 40 : 36;
  const team = player?.team || 'FA';
  const positions = Array.isArray(player?.positions) && player.positions.length > 0
    ? player.positions
    : player?.position
      ? [player.position]
      : [];
  const positionsStr = positions.length > 0 ? positions.join(', ') : '—';

  const prospectRankings = player?.prospectRankings || player?.stats?.prospectRankings || player?.stats?.rankings;
  const prospectSources = prospectRankings && typeof prospectRankings === 'object'
    ? Object.entries(prospectRankings)
        .map(([source, rank]) => ({ source, rank }))
        .sort((a, b) => a.rank - b.rank)
    : [];
  const topProspect = prospectSources[0];
  const prospectEligible = player?.prospectEligible || player?.stats?.prospectEligible;

  const content = player ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {/* Headshot - rounded rectangle, grey placeholder when no image */}
      <div
        style={{
          width: headshotSize,
          height: headshotSize,
          borderRadius: '6px',
          backgroundColor: '#e0e0e0',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {showPhoto ? (
          <img
            src={photoUrl}
            alt=""
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
            onError={() => setImgError(true)}
          />
        ) : (
          <PlayerSilhouette style={{ color: '#9aa2ab' }} />
        )}
      </div>
      {/* Name, team, positions */}
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontWeight: '600',
            fontSize: size === 'sm' ? 12 : size === 'lg' ? 15 : 13,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            cursor: onPlayerClick ? 'pointer' : 'default',
            color: onPlayerClick ? '#007bff' : '#333',
            textDecoration: onPlayerClick ? 'underline' : 'none',
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (onPlayerClick && player && leagueId) {
              onPlayerClick(player, leagueId);
            }
          }}
          onKeyDown={(e) => {
            if (onPlayerClick && player && leagueId && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              e.stopPropagation();
              onPlayerClick(player, leagueId);
            }
          }}
          role={onPlayerClick ? 'button' : undefined}
          tabIndex={onPlayerClick ? 0 : undefined}
        >
          {player.name}
        </div>
        <div
          style={{
            fontSize: size === 'sm' ? 10 : 11,
            color: '#666',
            marginTop: '1px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            flexWrap: 'wrap',
          }}
        >
          <span
            style={{
              padding: '1px 4px',
              backgroundColor: '#e8f4f8',
              borderRadius: '3px',
              fontWeight: '500',
              fontSize: size === 'sm' ? 9 : 10,
            }}
          >
            {team}
          </span>
          <span style={{ color: '#999' }}>·</span>
          <span>{positionsStr}</span>
        </div>
        {(topProspect || prospectEligible) && (
          <div
            style={{
              marginTop: '4px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              flexWrap: 'wrap',
            }}
          >
            <span
              style={{
                backgroundColor: '#eaf4ff',
                color: '#0d6efd',
                padding: '2px 6px',
                borderRadius: '999px',
                fontSize: size === 'sm' ? 9 : 10,
                fontWeight: 600,
              }}
            >
              {topProspect ? `${topProspect.source} #${topProspect.rank}` : 'Prospect Eligible'}
            </span>
            {topProspect && prospectSources.length > 1 && (
              <span style={{ fontSize: size === 'sm' ? 9 : 10, color: '#6c757d' }}>
                +{prospectSources.length - 1}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  ) : (
    <div style={{ fontStyle: 'italic', color: '#999', fontSize: 12 }}>
      {emptyLabel}
    </div>
  );

  if (asTd) {
    return <td style={tdStyle}>{content}</td>;
  }
  return content;
}

export default PlayerTableCell;
